import type { Effect } from '@flobi/artwork-core';
import type { CanvasHandle } from '../useEditorCanvas';
import { LOCAL_ORIGIN, type OpenDocument } from '../useEditorDocument';
import { terminal, type Binding, type GenerationClient } from '../ai/contracts';
import { generationQuote } from '../ai/pricing';
import { adjustValues } from './adjust';
import type { ImageToolHost } from './ToolViews';
import { sourceOf, startToolRun, toolRequest } from './run';
import { focusComposite } from './focus';
import { FOCUS_BLUR_BY } from './catalogue';
import { replacePicture } from './landResult';
import { readableMediaUrl } from '../../services/mediaRead';

/** What the picture and clip tools are given: the file, the canvas and the run client. */
export function imageToolHost({ open, canvas, client, notice, toVideo }: {
  open: OpenDocument; canvas: CanvasHandle; client: GenerationClient; notice(message: string): void; toVideo(id: string): void;
}): ImageToolHost & { onRunsChange(listener: () => void): () => void } {
  const runs = () => open.root.getMap<Binding>('artworkAiRuns');
  return {
    picture: id => {
      const source = sourceOf(open, id);
      if (!source) return null;
      return { src: source.asset.src!, width: source.asset.width || source.node.width, height: source.asset.height || source.node.height, video: !!source.node.clip || source.asset.kind === 'video', mediaAssetId: source.asset.mediaAssetId };
    },
    adjust: id => adjustValues(open.scene.get(id)),
    setAdjust: (id, values) => canvas.setAdjust(id, values),
    blur: id => {
      const effects = ((open.scene.get(id) as { effects?: Effect[] } | undefined)?.effects ?? []);
      const blur = effects.find(effect => effect.type === 'layer-blur') as { radius?: number } | undefined;
      return blur?.radius ?? 0;
    },
    setBlur: (id, radius) => canvas.setBlur(id, radius),
    quote: async (id, tool, choices) => {
      if (tool.local || !tool.model) return null;
      const request = await toolRequest(open, client, id, tool, choices, true);
      return client.quote(generationQuote(request));
    },
    run: async (id, tool, choices) => {
      if (open.scene.lockedHere(id)) throw Error('Unlock the picture first.');
      const request = await toolRequest(open, client, id, tool, choices, false, (layerId, longEdge) => canvas.renderLayerPng(layerId, longEdge));
      const quote = await client.quote(generationQuote(request)).catch(() => null);
      return startToolRun(open, client, id, tool, request, quote?.seconds, choices.strength);
    },
    running: id => {
      for (const run of runs().values()) if (run.targetId === id && run.tool && !terminal(run.status)) return run.tool.label;
      return null;
    },
    runOf: key => runs().get(key) ?? null,
    // Focus again at another strength, from the cut-out it already made: composed here, no new run.
    refocus: async (id, strength) => {
      let last: Binding | null = null;
      for (const run of runs().values()) if (run.targetId === id && run.tool?.id === 'focus' && run.applied && run.results[0] && (!last || run.createdAt > last.createdAt)) last = run;
      const sent = last?.request.references[0];
      if (!last || !sent) throw Error('Run Focus on this picture first.');
      const composed = await focusComposite(await readableMediaUrl(sent.url, sent.mediaAssetId), await readableMediaUrl(last.results[0]!.url, last.results[0]!.mediaAssetId), FOCUS_BLUR_BY[strength]);
      const saved = await client.reference(composed, `${last.targetName} focus.png`, 'canvas', id);
      if (!saved.mediaAssetId) throw Error('The focused picture could not be saved.');
      replacePicture(open, id, { mediaAssetId: saved.mediaAssetId, url: saved.url, width: saved.width, height: saved.height, mime: saved.mime, name: `${last.targetName} focus` });
      const key = last.key, value = runs().get(key);
      if (value) open.root.transact(() => runs().set(key, { ...value, tool: { ...value.tool!, strength } }), LOCAL_ORIGIN);
    },
    undo: () => open.undo.undo(),
    toVideo,
    box: id => canvas.boxOf(id),
    onViewChange: listener => canvas.onViewChange(listener),
    say: notice,
    onRunsChange: listener => { const map = runs(); map.observe(listener); return () => map.unobserve(listener); },
  };
}
